import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Globe2 } from "lucide-react";
import { absUrl } from "@/lib/seo-jsonld";
import { I18nProvider, useI18n } from "@/lib/i18n";
import { track } from "@/lib/analytics";
import { startPerfMonitoring } from "@/lib/web-vitals";

import { setPurchaseAttribution } from "@/lib/purchase-attribution";
import { VISION_CARD_IMAGES, VISION_HERO_IMAGE, visionCopy } from "@/lib/vision-content";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const TITLE = "Vision & Mission — Freezing Food Inflation Worldwide | MealToken24";
const OG_TITLE = "Vision & Mission — The $1 Meal Engine";
const DESCRIPTION =
  "How MealToken24, Solana micro-payments and sustainable infrastructure protect global communities from food inflation — the $1 meal engine and its five-step global storyline.";
const OG_IMAGE = absUrl("/og-vision.jpg");

export const Route = createFileRoute("/vision")({
  component: VisionRoute,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "food inflation, $1 meal, MealToken24, Meal2, Meal4, Solana micro-payments, sustainable food infrastructure, RWA, vision, mission",
      },
      { property: "og:type", content: "article" },
      { property: "og:site_name", content: "Sustainable Food & Eco Network" },
      { property: "og:title", content: OG_TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: absUrl("/vision") },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "MealToken24 — Freezing food inflation worldwide with the $1 meal engine",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@Neutral_Frame" },
      { name: "twitter:title", content: OG_TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: absUrl("/vision") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: OG_TITLE,
          description: DESCRIPTION,
          url: absUrl("/vision"),
          isPartOf: { "@type": "WebSite", name: "MealToken24", url: absUrl("/") },
        }),
      },
    ],
  }),
});

function VisionRoute() {
  return (
    <I18nProvider>
      <VisionPage />
    </I18nProvider>
  );
}

export function VisionPage() {
  const { lang } = useI18n();
  const copy = visionCopy(lang);
  const total = copy.cards.length;

  const stripRef = useRef<HTMLUListElement | null>(null);
  const cardRefs = useRef<Array<HTMLElement | null>>([]);
  const seen = useRef<Set<number>>(new Set());
  const [active, setActive] = useState(0);

  const position = (i: number) =>
    copy.cardPosition.replace("{n}", String(i + 1)).replace("{total}", String(total));

  const focusCard = useCallback(
    (index: number, source: "keyboard" | "button") => {
      const i = Math.max(0, Math.min(total - 1, index));
      const el = cardRefs.current[i];
      if (!el) return;
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      setActive(i);
      track("vision_card_view", { step: i + 1, source });
    },
    [total],
  );

  // Field performance monitoring (LCP / CLS / image load timings) with alert
  // thresholds, so post-deploy regressions surface in analytics too.
  useEffect(() => startPerfMonitoring("vision"), []);

  // Scroll / impression tracking — each card is reported once per page view.

  useEffect(() => {
    const cards = cardRefs.current.filter(Boolean) as HTMLElement[];
    if (cards.length === 0 || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) continue;
          const i = cards.indexOf(entry.target as HTMLElement);
          if (i < 0) continue;
          setActive(i);
          if (!seen.current.has(i)) {
            seen.current.add(i);
            track("vision_card_view", { step: i + 1, source: "scroll" });
          }
        }
      },
      { threshold: [0.6] },
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [total]);

  /** CTA -> purchase-flow attribution so end-to-end conversion is measurable. */
  const onCta = (id: string, token: "MEAL2" | "MEAL4") => {
    setPurchaseAttribution("vision_cta", token);
    track("cta_click", { id, surface: "vision_cta", token });
    track("vision_cta_click", { id, token, activeCard: active + 1 });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const keys = ["ArrowRight", "ArrowLeft", "Home", "End"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    if (e.key === "ArrowRight") focusCard(active + 1, "keyboard");
    else if (e.key === "ArrowLeft") focusCard(active - 1, "keyboard");
    else if (e.key === "Home") focusCard(0, "keyboard");
    else focusCard(total - 1, "keyboard");
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-5">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {copy.back}
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground sm:inline-flex">
              <Globe2 className="h-3.5 w-3.5" />
              {copy.eyebrow}
            </span>
            <LanguageSwitcher />
          </div>

        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <img
          src={VISION_HERO_IMAGE}
          alt={copy.heroAlt}
          width={1600}
          height={912}
          fetchPriority="high"
          loading="eager"
          sizes="100vw"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/80 to-background"
        />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center sm:py-32">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-300">
            {copy.eyebrow}
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            {copy.headline}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {copy.subheadline}
          </p>
        </div>
      </section>

      {/* FILMSTRIP */}
      <section className="relative py-16 sm:py-24" aria-labelledby="vision-strip-title">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {copy.stripEyebrow}
              </p>
              <h2
                id="vision-strip-title"
                className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl"
              >
                {copy.stripTitle}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">{copy.stripHint}</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => focusCard(active - 1, "button")}
                disabled={active === 0}
                aria-label={copy.prev}
                aria-controls="vision-filmstrip"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-foreground/80 transition-colors hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => focusCard(active + 1, "button")}
                disabled={active === total - 1}
                aria-label={copy.next}
                aria-controls="vision-filmstrip"
                className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-foreground/80 transition-colors hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <p aria-live="polite" className="sr-only">
          {position(active)}: {copy.cards[active]?.title}
        </p>

        <ul
          id="vision-filmstrip"
          data-vision-strip=""
          ref={stripRef}
          onKeyDown={onKeyDown}
          aria-roledescription={copy.stripRole}
          aria-label={copy.stripTitle}
          className="mt-10 flex list-none snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-6 pb-6 lg:px-[max(1.5rem,calc((100vw-72rem)/2))]"
        >
          {copy.cards.map((card, i) => (
            <li
              key={card.step}
              className="w-[85vw] max-w-[26rem] shrink-0 snap-center sm:w-[26rem]"
            >
              <article
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
                tabIndex={0}
                data-vision-card={i + 1}
                aria-roledescription="slide"
                aria-label={`${position(i)}: ${card.title}`}
                onFocus={() => setActive(i)}
                className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] transition-all duration-300 hover:-translate-y-1 hover:border-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={VISION_CARD_IMAGES[i]}
                    alt={card.alt}
                    width={1200}
                    height={900}
                    loading="lazy"
                    fetchPriority="low"
                    sizes="(max-width: 640px) 85vw, 26rem"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                  <span
                    aria-hidden
                    className="absolute left-4 top-4 rounded-full bg-black/60 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300 backdrop-blur"
                  >
                    {card.step}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-semibold tracking-tight">
                    {card.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
                </div>
              </article>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {copy.ctaTitle}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">{copy.ctaBody}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="/#buy-meal2"
              onClick={() => onCta("vision_buy_meal2", "MEAL2")}
              className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 px-7 py-3 text-sm font-bold uppercase tracking-wide text-obsidian shadow-[0_0_30px_-8px_rgba(52,211,153,0.7)] transition-transform hover:-translate-y-0.5 sm:w-auto"
            >
              {copy.ctaMeal2}
            </a>
            <a
              href="/#buy-meal4"
              onClick={() => onCta("vision_get_meal4", "MEAL4")}
              className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-br from-saffron to-coral px-7 py-3 text-sm font-bold uppercase tracking-wide text-obsidian shadow-[var(--glow-saffron)] transition-transform hover:-translate-y-0.5 sm:w-auto"
            >
              {copy.ctaMeal4}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
